import i18n from '@/Config/app/i18n';
import { SubscriptionIntervalEnum } from '@/Domain/enum/SubscriptionInterval.enum';
import { SubscriptionStatusEnum } from '@/Domain/enum/SubscriptionStatus.enum';
import { TierEnum } from '@/Domain/enum/Tier.enum';

export const SubscriptionHelper = {
  getTrierLabel: (tier: TierEnum, status?: SubscriptionStatusEnum, canceled?: boolean): string => {
    const config: Record<TierEnum, string> = {
      // Legacy paid tiers share a name with their new-pricing `_2026` counterpart; suffix "(Legacy)" so the Membership plan name disambiguates them.
      [TierEnum.BASIC]: i18n.t('subscription_page:tiers.basic_legacy'),
      [TierEnum.STARTER]: i18n.t('subscription_page:tiers.starter'),
      [TierEnum.TRIAL]: i18n.t('subscription_page:tiers.trial'),
      [TierEnum.ESSENTIAL]: i18n.t('subscription_page:tiers.essential_legacy'),
      [TierEnum.PROFESSIONAL]: i18n.t('subscription_page:tiers.professional_legacy'),
      [TierEnum.BUSINESS]: i18n.t('subscription_page:tiers.business_legacy'),
      [TierEnum.CUSTOM]: i18n.t('subscription_page:tiers.custom'),
      [TierEnum.LEGACY_LITE]: i18n.t('subscription_page:tiers.legacy_lite'),
      [TierEnum.LEGACY_ADDONS]: i18n.t('subscription_page:tiers.legacy_lite'),
      [TierEnum.FREE]: i18n.t('subscription_page:tiers.free'),
      [TierEnum.BASIC_2026]: i18n.t('subscription_page:tiers.basic'),
      [TierEnum.ESSENTIAL_2026]: i18n.t('subscription_page:tiers.essential'),
      [TierEnum.PROFESSIONAL_2026]: i18n.t('subscription_page:tiers.professional'),
      [TierEnum.BUSINESS_2026]: i18n.t('subscription_page:tiers.business'),
    };

    if (typeof canceled === 'boolean' && canceled) {
      return config[tier] + ` (${i18n.t('subscription_page:status.cancel')})`;
    }

    if (status && status === SubscriptionStatusEnum.Trialing) {
      return config[tier] + ` (${i18n.t('subscription_page:status.trial')})`;
    }

    return config[tier];
  },

  getIntervalLabel: (interval: SubscriptionIntervalEnum): string => {
    const config = {
      [SubscriptionIntervalEnum.Monthly]: i18n.t('subscription_page:interval.monthly'),
      [SubscriptionIntervalEnum.Annually]: i18n.t('subscription_page:interval.yearly'),
      [SubscriptionIntervalEnum.OneTime]: '',
    };

    return config[interval];
  },

  isTierHigher: (currentTier: TierEnum, tier: TierEnum): boolean => {
    const config: Record<TierEnum, number> = {
      [TierEnum.LEGACY_LITE]: 0,
      [TierEnum.LEGACY_ADDONS]: 0,
      [TierEnum.TRIAL]: 0,
      [TierEnum.BASIC]: 1,
      [TierEnum.ESSENTIAL]: 2,
      [TierEnum.PROFESSIONAL]: 3,
      [TierEnum.BUSINESS]: 4,
      // New pricing lineup, offset above legacy so any new plan reads as an upgrade for legacy users.
      [TierEnum.FREE]: 10,
      [TierEnum.STARTER]: 11,
      [TierEnum.BASIC_2026]: 12,
      [TierEnum.ESSENTIAL_2026]: 13,
      [TierEnum.PROFESSIONAL_2026]: 14,
      [TierEnum.BUSINESS_2026]: 15,
      [TierEnum.CUSTOM]: 16,
    };

    return config[tier] > config[currentTier];
  },

  isFreeAccount: (tier: TierEnum): boolean => tier === TierEnum.TRIAL || tier === TierEnum.FREE,

  // Legacy `trial` is the feature-identical predecessor of the new `free` plan; surface it as `free` on the billing page so the Free card reads as the current plan.
  getBillingDisplayTier: (tier: TierEnum): TierEnum =>
    tier === TierEnum.TRIAL ? TierEnum.FREE : tier,

  calculateDaysUntilRenewal: (subscription: any, usage: any): number | null => {
    let renewalDate = null;
    // For Trial plans, use messages_next_refreshed from usage
    if (SubscriptionHelper.isFreeAccount(subscription?.current_plan?.tier)) {
      renewalDate = usage?.messages_next_refreshed;
    } else {
      // For other plans, use next_payment_on from subscription
      renewalDate = subscription?.subscription?.next_payment_on;
    }
    if (!renewalDate) {
      return null;
    }

    // Parse the renewal date - handle custom format like 'Aug. 10, 2025, midnight'
    let nextRefreshDate = new Date(renewalDate);
    // If the date parsing failed, try to parse the custom format
    if (isNaN(nextRefreshDate.getTime()) && typeof renewalDate === 'string') {
      // Handle format like 'Aug. 10, 2025, midnight'
      const customFormatMatch = renewalDate.match(/^(\w{3})\. (\d{1,2}), (\d{4}), midnight$/);
      if (customFormatMatch) {
        const [, month, day, year] = customFormatMatch;
        const monthMap: { [key: string]: number } = {
          Jan: 0,
          Feb: 1,
          Mar: 2,
          Apr: 3,
          May: 4,
          Jun: 5,
          Jul: 6,
          Aug: 7,
          Sep: 8,
          Oct: 9,
          Nov: 10,
          Dec: 11,
        };
        const monthIndex = monthMap[month];
        if (monthIndex !== undefined) {
          nextRefreshDate = new Date(parseInt(year), monthIndex, parseInt(day), 0, 0, 0, 0);
        }
      }
    }

    const currentDate = new Date();

    // For Trial plans, always use simple date difference calculation
    if (SubscriptionHelper.isFreeAccount(subscription?.current_plan?.tier)) {
      // Check if the date parsing was successful
      if (isNaN(nextRefreshDate.getTime())) {
        return null;
      }

      const timeDiff = nextRefreshDate.getTime() - currentDate.getTime();
      const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
      return daysDiff > 0 ? daysDiff : null;
    }

    // For annual subscriptions, calculate days until an next monthly occurrence of the payment day
    if (subscription?.subscription?.interval === 'annually') {
      const paymentDay = nextRefreshDate.getDate();
      const currentYear = currentDate.getFullYear();
      const currentMonth = currentDate.getMonth();
      const currentDay = currentDate.getDate();

      // Calculate a next occurrence of the payment day
      let nextOccurrence;
      if (currentDay < paymentDay) {
        // Payment day hasn't occurred this month yet
        nextOccurrence = new Date(currentYear, currentMonth, paymentDay);
      } else {
        // Payment day has passed this month, get next month's occurrence
        nextOccurrence = new Date(currentYear, currentMonth + 1, paymentDay);
      }

      const timeDiff = nextOccurrence.getTime() - currentDate.getTime();
      const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));

      return daysDiff > 0 ? daysDiff : null;
    }

    // For non-annual subscriptions, use the original logic
    const timeDiff = nextRefreshDate.getTime() - currentDate.getTime();
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));

    return daysDiff > 0 ? daysDiff : null;
  },
};
